import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../../providers/supabase/supabase";

export type TranscriptNotice = {
  texto: string;
  firma: string;
  enlace_aviso: string | null;
  retencion_dias: number;
  version: number;
};

/** Plantilla del aviso de transcripción (Bloque C2, paso 2), editable en IA y ajustes. */
export const useTranscriptNotice = () =>
  useQuery({
    queryKey: ["crm_settings", "transcript_notice"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("crm_settings")
        .select("transcript_notice")
        .eq("id", 1)
        .single();
      if (error) throw error;
      return data.transcript_notice as TranscriptNotice;
    },
  });

export type TranscriptConsent = {
  id: number;
  given_at: string;
  stopped_at: string | null;
  transcript_deleted_at: string | null;
  transcript_text: string | null;
};

/** Un consentimiento detenido no habilita nada (captura, checkbox): hay que dar uno nuevo. */
export const hasActiveTranscriptConsent = (
  consent: TranscriptConsent | null | undefined,
): boolean => !!consent && !consent.stopped_at;

/** El consentimiento más reciente para esta oportunidad, si ya se registró. */
export const useTranscriptConsent = (dealId: number | null) =>
  useQuery({
    queryKey: ["transcript_consent", dealId],
    enabled: !!dealId,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("transcript_consents")
        .select(
          "id, given_at, stopped_at, transcript_deleted_at, transcript_text",
        )
        .eq("deal_id", dealId!)
        .order("given_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as TranscriptConsent | null;
    },
  });

/** Registra "ya avisé y la persona aceptó" (quién, cuándo, a qué contacto y qué versión). */
export const giveTranscriptConsent = async (input: {
  contactId: number;
  companyId: number | null;
  dealId: number | null;
  callSessionId: number | null;
  salesId: number;
  noticeText: string;
  noticeVersion: number;
}) => {
  const { error } = await getSupabaseClient()
    .from("transcript_consents")
    .insert({
      contact_id: input.contactId,
      company_id: input.companyId,
      deal_id: input.dealId,
      call_session_id: input.callSessionId,
      sales_id: input.salesId,
      notice_version: input.noticeVersion,
      notice_text: input.noticeText,
    });
  if (error) throw new Error(error.message);
};

export const stopTranscript = async (consentId: number) => {
  const { error } = await getSupabaseClient()
    .from("transcript_consents")
    .update({ stopped_at: new Date().toISOString() })
    .eq("id", consentId);
  if (error) throw new Error(error.message);
};

/** "Borrar transcripción de esta llamada": borra el texto, conserva el registro del consentimiento. */
export const deleteTranscriptText = async (consentId: number) => {
  const { error } = await getSupabaseClient()
    .from("transcript_consents")
    .update({
      transcript_text: null,
      transcript_deleted_at: new Date().toISOString(),
    })
    .eq("id", consentId);
  if (error) throw new Error(error.message);
};
